import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, mediaUrl } from "@/lib/api";
import { Trash2, Plus, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : "x" + Date.now() + Math.random().toString(16).slice(2));

const Flag = ({ code }) => (
  <span className="mr-1 rounded bg-[#0A192F]/8 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#0A192F]">
    {code}
  </span>
);

// Single plain text field
export const PlainField = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs font-semibold text-[#0A192F]">{label}</Label>}
    <Input type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </div>
);

// Bilingual EN/FR field. value = { en, fr }
export const BilingualField = ({ label, value = {}, onChange, textarea }) => {
  const Comp = textarea ? Textarea : Input;
  const upd = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs font-semibold text-[#0A192F]">{label}</Label>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex items-start gap-1.5">
          <span className="pt-2"><Flag code="EN" /></span>
          <Comp value={value.en || ""} onChange={upd("en")} rows={textarea ? 3 : undefined} />
        </div>
        <div className="flex items-start gap-1.5">
          <span className="pt-2"><Flag code="FR" /></span>
          <Comp value={value.fr || ""} onChange={upd("fr")} rows={textarea ? 3 : undefined} />
        </div>
      </div>
    </div>
  );
};

// List of bilingual strings. value = [{en,fr}, ...]
export const StringList = ({ label, value = [], onChange }) => {
  const update = (i, v) => onChange(value.map((it, idx) => (idx === i ? v : it)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...(value || []), { en: "", fr: "" }]);
  return (
    <div className="space-y-2">
      {label && <Label className="text-xs font-semibold text-[#0A192F]">{label}</Label>}
      {(value || []).map((it, i) => (
        <div key={i} className="flex items-start gap-2 rounded-lg border border-[#0A192F]/8 p-2">
          <div className="flex-1">
            <BilingualField value={it} onChange={(v) => update(i, v)} />
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="text-[#C62828]">
            <Trash2 size={16} />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5">
        <Plus size={14} /> Add
      </Button>
    </div>
  );
};

// List of objects. renderItem(item, update(patch), index)
export const ObjectList = ({ label, value = [], onChange, template, renderItem, itemTitle }) => {
  const update = (i, patch) => onChange(value.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...(value || []), { id: uid(), ...template() }]);
  return (
    <div className="space-y-3">
      {label && <h3 className="text-sm font-bold text-[#0A192F]">{label}</h3>}
      {(value || []).map((it, i) => (
        <div key={it.id || i} className="rounded-xl border border-[#0A192F]/10 bg-[#FAFAF8] p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              {itemTitle ? itemTitle(it, i) : `Item ${i + 1}`}
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} className="gap-1 text-[#C62828]">
              <Trash2 size={14} /> Remove
            </Button>
          </div>
          <div className="space-y-3">{renderItem(it, (patch) => update(i, patch), i)}</div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5">
        <Plus size={14} /> Add
      </Button>
    </div>
  );
};

// File upload → returns a URL stored in content. Supports images and PDFs.
export const FileUploadField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const isImg = value && !/\.pdf($|\?)/i.test(value);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs font-semibold text-[#0A192F]">{label}</Label>}
      <div className="flex items-center gap-3">
        {value ? (
          isImg ? (
            <img src={mediaUrl(value)} alt="preview" className="h-16 w-16 rounded-lg object-cover" />
          ) : (
            <span className="grid h-16 w-16 place-items-center rounded-lg bg-[#0A192F]/8 text-[#0A192F]">
              <FileText size={22} />
            </span>
          )
        ) : (
          <span className="grid h-16 w-16 place-items-center rounded-lg border border-dashed border-[#0A192F]/20 text-[#64748B]">
            <Upload size={18} />
          </span>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-[#0A192F] px-4 py-2 text-xs font-semibold text-white">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading…" : "Upload image / PDF"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handle} disabled={busy} />
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-left text-xs text-[#C62828]">
              Remove file
            </button>
          )}
        </div>
      </div>
      {value && <PlainField value={value} onChange={onChange} placeholder="File URL" />}
    </div>
  );
};
